import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { BehaviorSubject } from "rxjs"

/**
 * Each time a new storageKey is addressed, a new BehaviorSubject is built.
 * Then, any call to useLocalStorage will reuse the same observable, so updating the localStorage
 * in a component will rerender other components that rely on it, while persisting the state in storage.
 */
const localStorageObservables: { [storageKey: string]: BehaviorSubject<string> } = {}

export function useLocalStorage<T extends string> (storageKey: string, defaultValue?: T): [T, (value: T) => void] {
  // use setState, so react is aware when to render the caller component (when this data changes)
  const [value, setValue] = useState(localStorage.getItem(storageKey) as T)

  // use a unique observable per storageKey, so every component listening the same `storageKey` will register a shared state
  localStorageObservables[storageKey] ||= new BehaviorSubject(value as string)

  useEffect(() => {
    // update the caller component when any intance of the hook for that `storageKey` does a mutation.
    const subscription = localStorageObservables[storageKey].subscribe(setValue as Dispatch<SetStateAction<string>>)
    return () => subscription.unsubscribe()
  }, [storageKey])

  return [value ?? defaultValue, newValue => {
    localStorage.setItem(storageKey, newValue)
    localStorageObservables[storageKey].next(newValue)
  }]
}

export function useLocalStorageBoolean (storageKey: string, defaultValue = false): [boolean, (value: boolean) => void] {
  const [value, setValue] = useLocalStorage(storageKey, defaultValue ? 'true' : 'false')
  return [value === 'true', newValue => setValue(newValue ? 'true' : 'false')]
}
